import { useQuery } from '@apollo/client';
import type { Provider as ProvisionProvider } from '@newfront-insurance/react-provision';
import { useProvider } from '@newfront-insurance/react-provision';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { GET_ACCOUNT } from '../queries';
import { NovaHostBridge, type NovaHostBridgeFatalReason, type NovaHostBridgeTelemetry } from './bridge';
import { IframeHost } from './iframe-host';
import type { HostApp, NovaHostContextPatch, NovaLauncherSize, NovaStateMessage } from './protocol';

export interface NovaLoaderAuthContext {
  userDetails?: { uuid?: string };
}

export interface NovaChatOverlayProps<TAuthContext extends NovaLoaderAuthContext = NovaLoaderAuthContext> {
  authProvider: ProvisionProvider<TAuthContext>;
  hostApp: HostApp;
  transparentFloatingButton?: boolean;
  initialAccountUuid?: string;
  contextPatch?: NovaHostContextPatch;
  embedPath?: string;
  onFatalError?(reason: NovaHostBridgeFatalReason): void;
  onTelemetry?(event: NovaHostBridgeTelemetry): void;
}

const EMPTY_STATE: NovaStateMessage['payload'] = {
  enabled: false,
  open: false,
  dimension: 'panel',
  hasNotifications: false,
};
const EMPTY_CONTEXT_PATCH: NovaHostContextPatch = {};

export function getClampedLauncherRight(right: number, viewportWidth: number, launcher: NovaLauncherSize): number {
  const minimumRight = launcher.inset?.right ?? 0;
  return Math.max(minimumRight, Math.min(right, viewportWidth - launcher.width));
}

export function NovaChatOverlay<TAuthContext extends NovaLoaderAuthContext>({
  authProvider,
  hostApp,
  transparentFloatingButton,
  initialAccountUuid,
  contextPatch = EMPTY_CONTEXT_PATCH,
  embedPath,
  onFatalError,
  onTelemetry,
}: NovaChatOverlayProps<TAuthContext>) {
  const { userDetails } = useProvider(authProvider);
  const router = useRouter();
  if (!userDetails?.uuid || router.pathname.startsWith('/chat/debug')) return null;

  return (
    <NovaChatIframe
      key={`${hostApp}:${userDetails.uuid}`}
      hostApp={hostApp}
      transparentFloatingButton={transparentFloatingButton}
      initialAccountUuid={initialAccountUuid}
      contextPatch={contextPatch}
      embedPath={embedPath}
      onFatalError={onFatalError}
      onTelemetry={onTelemetry}
    />
  );
}

// A user change or logout disposes of the entire bridge session and its listeners.
function NovaChatIframe({
  hostApp,
  transparentFloatingButton,
  initialAccountUuid,
  contextPatch = EMPTY_CONTEXT_PATCH,
  embedPath,
  onFatalError,
  onTelemetry,
}: Omit<NovaChatOverlayProps, 'authProvider'>) {
  const router = useRouter();
  const [state, setState] = useState(EMPTY_STATE);
  const [failure, setFailure] = useState<NovaHostBridgeFatalReason | null>(null);
  const [attempt, setAttempt] = useState(0);
  const callbacksRef = useRef({ onFatalError, onTelemetry });
  useLayoutEffect(() => {
    callbacksRef.current = { onFatalError, onTelemetry };
  }, [onFatalError, onTelemetry]);
  const [launcherRight, setLauncherRight] = useState(36);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const launcherRightRef = useRef(launcherRight);
  const launcherSizeRef = useRef<NovaLauncherSize>({ width: 184, height: 40 });
  const previousOpenRef = useRef(false);
  const iframeId = `nova-assist-iframe-${hostApp}`;
  const launcherStorageKey = `nova-button-position-${hostApp}`;

  useEffect(() => {
    const stored = Number.parseInt(window.localStorage.getItem(launcherStorageKey) ?? '', 10);
    if (!Number.isFinite(stored)) return undefined;
    const frame = window.requestAnimationFrame(() => {
      launcherRightRef.current = stored;
      setLauncherRight(stored);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [launcherStorageKey]);

  useEffect(() => {
    if (state.launcher) launcherSizeRef.current = state.launcher;
  }, [state.launcher]);

  const clampLauncherRight = useCallback((right: number) => {
    return getClampedLauncherRight(right, window.innerWidth, launcherSizeRef.current);
  }, []);
  const handleLauncherMove = useCallback(
    ({ deltaX, persist }: { deltaX: number; persist: boolean }) => {
      const next = clampLauncherRight(launcherRightRef.current + deltaX);
      launcherRightRef.current = next;
      setLauncherRight(next);
      if (persist) window.localStorage.setItem(launcherStorageKey, String(next));
    },
    [clampLauncherRight, launcherStorageKey],
  );

  useEffect(() => {
    let frame = 0;
    const clamp = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const next = clampLauncherRight(launcherRightRef.current);
        if (next === launcherRightRef.current) return;
        launcherRightRef.current = next;
        setLauncherRight(next);
        window.localStorage.setItem(launcherStorageKey, String(next));
      });
    };
    clamp();
    window.addEventListener('resize', clamp);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', clamp);
    };
  }, [clampLauncherRight, launcherStorageKey, state.launcher]);

  const handleBridgeFailure = useCallback((reason: NovaHostBridgeFatalReason) => {
    setFailure(reason);
  }, []);
  const handleBridgeTelemetry = useCallback((event: NovaHostBridgeTelemetry) => {
    callbacksRef.current.onTelemetry?.(event);
  }, []);
  const createBridge = useCallback(
    () =>
      new NovaHostBridge({
        hostApp,
        requiredCapabilities: ['remote-launcher'],
        onState: setState,
        onFatalError: handleBridgeFailure,
        onTelemetry: handleBridgeTelemetry,
      }),
    [hostApp, handleBridgeFailure, handleBridgeTelemetry],
  );
  // eslint-disable-next-line react-hooks/refs -- The constructor stores callbacks; refs are read only by message/timer handlers.
  const [bridge, setBridge] = useState(createBridge);

  useLayoutEffect(() => {
    if (failure) return undefined;
    bridge.setLauncherMoveHandler(handleLauncherMove);
    bridge.start();
    return () => {
      bridge.setLauncherMoveHandler(undefined);
      bridge.stop();
    };
  }, [bridge, failure, handleLauncherMove]);

  // Retry once per mounted user session, even if that retry handshakes successfully.
  // A later error stays terminal to avoid an unbounded reload loop.
  useEffect(() => {
    if (!failure) return undefined;
    if (failure !== 'nova-error' || attempt >= 1) {
      callbacksRef.current.onFatalError?.(failure);
      return undefined;
    }
    callbacksRef.current.onTelemetry?.({ name: 'retry', data: { reason: failure } });
    const timeout = window.setTimeout(() => {
      setBridge(createBridge());
      setState(EMPTY_STATE);
      setFailure(null);
      setAttempt((previous) => previous + 1);
    }, 1000);
    return () => window.clearTimeout(timeout);
  }, [attempt, createBridge, failure]);

  const queryAccountUuid = normalizeQueryValue(router.query.accountUuid);
  const requestedPageAccountUuid = pickValidUuid(initialAccountUuid) ?? pickValidUuid(queryAccountUuid);
  const { data, loading, error } = useQuery(GET_ACCOUNT, {
    variables: { uuid: requestedPageAccountUuid ?? '' },
    skip: !requestedPageAccountUuid || 'accountUuid' in contextPatch,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  // Preserve the protected account bootstrap: an inaccessible page must not become chat context.
  const pageAccountUuid =
    'accountUuid' in contextPatch
      ? pickValidUuid(contextPatch.accountUuid ?? undefined)
      : !loading && !error && data?.getAccount?.uuid === requestedPageAccountUuid
        ? requestedPageAccountUuid
        : undefined;
  useEffect(() => {
    bridge.setContextPatch({ ...contextPatch, accountUuid: pageAccountUuid ?? null });
  }, [bridge, contextPatch, pageAccountUuid]);

  useEffect(() => {
    if (failure) return undefined;
    const updateVisibility = () => {
      bridge.setVisibility({ visible: document.visibilityState === 'visible', focused: document.hasFocus() });
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'KeyK' && (event.metaKey || event.ctrlKey) && state.enabled) {
        event.preventDefault();
        bridge.sendToggle();
      } else if (event.key === 'Escape' && state.open && state.dimension === 'panel') {
        bridge.sendClose();
      }
    };
    updateVisibility();
    document.addEventListener('visibilitychange', updateVisibility);
    window.addEventListener('focus', updateVisibility);
    window.addEventListener('blur', updateVisibility);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('visibilitychange', updateVisibility);
      window.removeEventListener('focus', updateVisibility);
      window.removeEventListener('blur', updateVisibility);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [bridge, failure, state.dimension, state.enabled, state.open]);

  useEffect(() => {
    if (state.open && !previousOpenRef.current && bridge.isHandshakeComplete()) iframeRef.current?.focus();
    previousOpenRef.current = state.open;
  }, [bridge, state.open]);

  if (failure) return null;

  return (
    <IframeHost
      key={attempt}
      iframeId={iframeId}
      iframeRef={iframeRef}
      embedPath={embedPath}
      dimension={state.dimension}
      open={state.enabled && state.open}
      launcher={state.enabled ? state.launcher : undefined}
      launcherRight={launcherRight}
      transparentLauncher={transparentFloatingButton}
      onMount={(iframe) => bridge.attachIframe(iframe)}
      onLoad={() => bridge.notifyIframeLoaded()}
    />
  );
}

function normalizeQueryValue(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

function pickValidUuid(value: string | undefined): string | undefined {
  // Keep this aligned with nova-core's account-bootstrap isUuid predicate.
  return value && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(value) ? value : undefined;
}
